import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["All", "Pending Invoice", "Invoice Sent", "Awaiting Payment", "Paid", "Preparing", "Shipped", "Delivered", "Cancelled", "Refunded"];

const STATUS_STYLES = {
  "Pending Invoice": "bg-amber-50 text-amber-700 border-amber-200",
  "Invoice Sent": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Awaiting Payment": "bg-orange-50 text-orange-700 border-orange-200",
  "Paid": "bg-blue-50 text-blue-700 border-blue-200",
  "Preparing": "bg-slate-100 text-slate-700 border-slate-200",
  "Shipped": "bg-sky-50 text-sky-700 border-sky-200",
  "Delivered": "bg-blue-50 text-blue-700 border-blue-200",
  "Cancelled": "bg-red-50 text-red-700 border-red-200",
  "Refunded": "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // {order_id, customer_name, total}
  const nav = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "All") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/orders${params.toString() ? "?" + params.toString() : ""}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const doDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.order_id;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success(`Order ${id} deleted`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Orders</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">All orders</h1>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-9 w-56"/>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            className={`h-8 px-3 rounded-md text-xs border transition-colors ${status === s ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Order #</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Total</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr
                key={o.order_id}
                className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                onClick={() => nav(`/admin/orders/${o.order_id}`)}
                data-testid={`admin-order-${o.order_id}`}
              >
                <td className="px-5 py-4 font-mono text-slate-900">{o.order_id}</td>
                <td className="px-5 py-4">
                  <div className="text-slate-900">{o.customer_name}</div>
                  <div className="text-xs text-slate-500">{o.customer_email}</div>
                </td>
                <td className="px-5 py-4 text-slate-600">{new Date(o.placed_at).toLocaleDateString()}</td>
                <td className="px-5 py-4">
                  <Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLES[o.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{o.status}</Badge>
                </td>
                <td className="px-5 py-4 text-right font-mono">${Number(o.total).toFixed(2)}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <Link
                    to={`/admin/orders/${o.order_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-blue-700 hover:text-blue-800 mr-3"
                  >
                    Open →
                  </Link>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ order_id: o.order_id, customer_name: o.customer_name, total: o.total }); }}
                    data-testid={`admin-order-delete-${o.order_id}`}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500"
                    aria-label={`Delete order ${o.order_id}`}
                    title="Delete order"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pendingDelete && (
        <div data-testid="admin-order-delete-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white border border-slate-200 rounded-md max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" strokeWidth={1.75}/>
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">Delete this order?</div>
                <div className="text-xs text-slate-500">This cannot be undone.</div>
              </div>
            </div>
            <div className="mt-4 border border-slate-200 rounded-md p-3 bg-slate-50 text-sm">
              <div><span className="font-mono text-slate-500">Order</span> · <span className="font-mono text-slate-900">{pendingDelete.order_id}</span></div>
              <div><span className="font-mono text-slate-500">Customer</span> · {pendingDelete.customer_name || "—"}</div>
              <div><span className="font-mono text-slate-500">Total</span> · ${Number(pendingDelete.total || 0).toFixed(2)}</div>
            </div>
            <p className="mt-3 text-xs text-slate-600">
              The order, its invoice record, and any associated PDF will be permanently removed from the database.
              It will disappear from the customer dashboard, admin lists, and all order counts.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                data-testid="admin-order-delete-cancel"
                className="h-9 px-4 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                data-testid="admin-order-delete-confirm"
                className="h-9 px-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm"
              >
                Delete order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
